import { useEffect, useState, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import { Link } from "react-router-dom";
import socket, { joinChat } from '../shared/_helpers/socket';
import moment from "moment";
import { getAdvisorListFromClientForChat, createChatFromClientSide, getLastMessageData, getClientMessages, uploadDocument } from "../shared/_services/common.services";
import chatPlaceholder from "../assets/images/chatPlaceholder.png";

const UserChatScreen = ({ onClose, showModal }) => {
  const [selectedAdvisorId, setSelectedAdvisorId] = useState(null);
  const [chatId, setChatId] = useState("");
  
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [typingStatus, setTypingStatus] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [showDocumentList, setShowDocumentList] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const sender = JSON.parse(localStorage.getItem('currentUser'))?.user._id;
  const lastMessageRef = useRef(null);
  const fileInputRef = useRef(null);


  useEffect(() => {
    const fetchChatData = async () => {
      try {
        const response = await getAdvisorListFromClientForChat();
        // Add defensive checks
        const advisorsData = response?.data?.data;
        if (advisorsData) {
          const { previouslyChattedAdvisors = [], newAdvisors = [] } = advisorsData;

          if (previouslyChattedAdvisors.length > 0) {
            setSelectedAdvisorId(previouslyChattedAdvisors[0]?._id);
          } else if (newAdvisors.length > 0) {
            setSelectedAdvisorId(newAdvisors[0]?._id);
          }
        } else {
          console.error("Unexpected API response format", response);
        }
      } catch (error) {
        console.error("Error fetching chat data", error);
      }
    };

    fetchChatData();
  }, []);


  useEffect(() => {
    const fetchClientChatData = async () => {
      try {
        const response = await getClientMessages();
        const allMessages = response?.data?.data
          ?.map((item) => item?.messages)
          .flat();
        setMessages(allMessages || []);
      } catch (error) {
        console.error("Error fetching chat data", error);
      }
    };

    if (showModal) {
      fetchClientChatData();
    }
  }, [showModal]); // Modal ke open hone par hi data fetch karein

  useEffect(() => {
    if (showModal && selectedAdvisorId) {
      createChatFromClientSide({ participantId: selectedAdvisorId })
        .then((response) => {
          const { _id: chattId } = response?.data?.data;
          
          setChatId(chattId);

         
            getLastMessageData()
              .then((lastMessageResponse) => {
                
              })
              .catch(console.error);
          
        })
        .catch(console.error);
    }
  }, [showModal, selectedAdvisorId]);

  useEffect(() => {
    if (chatId) {
      joinChat(chatId);
    }

    socket.on('message received', (newMessage) => {
      const incoming = Array.isArray(newMessage) ? newMessage : [newMessage];
      setMessages((prevMessages) => [...prevMessages, ...incoming]);
    });
    socket.on('typing', (status) => {
      setTypingStatus(status);

      setTimeout(() => {
        setTypingStatus('');
      }, 3000);
    });

    return () => {
      socket.off('message received');
      socket.off('typing');
    };
  }, [chatId]);

  const handleSendMessage = async () => {
    console.log("Message input:", messageInput);

    if (!messageInput.trim() && attachedFiles.length === 0) {
      console.log("Empty message and no files, not sending");
      return;
    }

    if (!chatId) {
      console.log("No chatId available yet, cannot send message");
      return;
    }

    // If there are files to upload, disable send button and show progress
    if (attachedFiles.length > 0) {
      setIsUploading(true);
    }

    // 1) Send text message if present
    if (messageInput.trim()) {
      const textPayload = {
        chatId,
        sender,
        content: {
          message: messageInput.trim(),
          type: "text",
          url: ""
        }
      };

      console.log("Emitting text payload:", textPayload);
      socket.emit("new message", [textPayload]);
    }

    // 2) Upload attached documents (if any) and send as file messages
    if (attachedFiles.length > 0) {
      try {
        const form = new FormData();
        attachedFiles.forEach((file) => form.append("files", file));

        const res = await uploadDocument(form, "chat");
        const uploaded = res?.data?.data || [];

        uploaded.forEach((doc) => {
          const filePayload = {
            chatId,
            sender,
            content: {
              message: doc?.originalname || "file",
              type: "file",
              url: doc?.file_url || "",
              doc_id: doc?.doc_id
            }
          };

          console.log("Emitting file payload:", filePayload);
          socket.emit("new message", [filePayload]);
        });
      } catch (err) {
        console.error("Error uploading documents from chat:", err);
      } finally {
        setIsUploading(false);
      }
    }

    // Reset input and local attachments
    setMessageInput("");
    setAttachedFiles([]);
    setShowDocumentList(false);
  };
  

  const handleTyping = () => {
    if (chatId) {
      socket.emit('typing', { chatId });
    }
  };

  // File attach handlers
  const handleAttachClick = () => {
    if (isUploading) return; // Prevent attaching during upload
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.xls', '.xlsx', '.csv', '.doc', '.docx'];
    const maxSize = 26214400; // 25 MB

    const currentCount = attachedFiles.length;
    const availableSlots = Math.max(0, 10 - currentCount);
    const limitedFiles = files.slice(0, availableSlots);

    const validFiles = limitedFiles.filter((file) => {
      const lowerName = file.name.toLowerCase();
      const hasAllowedExt = allowedExtensions.some((ext) => lowerName.endsWith(ext));
      const isValidSize = file.size <= maxSize;

      if (!hasAllowedExt) {
        console.warn("File type not allowed:", file.name);
        return false;
      }

      if (!isValidSize) {
        console.warn("File too large (>25MB):", file.name);
        return false;
      }

      return true;
    });

    setAttachedFiles((prev) => [...prev, ...validFiles]);

    // reset input so same file can be re-selected later if needed
    e.target.value = null;
  };

  const handleRemoveAttachedFile = (indexToRemove) => {
    if (isUploading) return; // Prevent removal during upload
    setAttachedFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleToggleDocumentList = () => {
    setShowDocumentList((prev) => !prev);
  };

  const handleClose = () => {
    onClose(false);
  };

  const formatDate = (dateString) => {
    const date = moment(dateString);
    if (date.isSame(moment(), "day")) {
      return "Today";
    } else if (date.isSame(moment().subtract(1, "day"), "day")) {
      return "Yesterday";
    } else {
      return date.format("DD MMMM YYYY");
    }
  };

  // Track the last displayed date
  let lastDisplayedDate = null;

   // Scroll to the last message whenever the messages array changes
   useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  return (
    <Modal className="userChatPopup notificationPopup fade-right"
      show={showModal}
      onHide={handleClose}
      aria-labelledby="notificationPopupLabel"
      keyboard={false}
      centered
    >
      <div className="modal-body">
        <div className="d-flex justify-content-between p-3">
          {/* <h4>Message</h4> */}
          <h4 className="ast-card-heading w-100 mb-2">Message</h4>
          <Link className="close-popup-btn" onClick={handleClose}>
            <i className="iconoir-xmark"></i>
          </Link>
        </div>
        <div className="notifications-body">
          <div className="user-chat-placeholder p-3" style={{ display: messages?.length === 0 ? "block" : "none" }}>
            <h2>Welcome to astra chat!</h2>
            <img src={chatPlaceholder} />
            <p>Thank you for your query. We'll respond as soon as the financial advisor is available.</p>
          </div>
          <div className="user-chat-body mini-scroll-bar" style={{ display: messages?.length > 0 ? "block" : "none" }}>
            <div className="user-chat-message">
              {[...messages]
                .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)) // Sort by createdAt
                .map((msg, index) => {
                  const messageDate = formatDate(msg.createdAt);
                  const showDate = messageDate !== lastDisplayedDate;
                  lastDisplayedDate = messageDate;

                  const isMessageSent = msg.sender === sender || msg.sender?.userType === "client";

                  // Handle both old string content and new object content { message, type, url }
                  const rawContent = msg?.content;
                  const messageText =
                    typeof rawContent === "string"
                      ? rawContent
                      : rawContent?.message || "";
                  const messageType =
                    typeof rawContent === "string"
                      ? "text"
                      : rawContent?.type || "text";
                  const messageUrl =
                    typeof rawContent === "string"
                      ? ""
                      : rawContent?.url || "";

                  return (
                    <>
                      {showDate && <p className="msg-date">{messageDate}</p>}
                      <div className={isMessageSent ? "message-sent" : "message-rcvd"}>
                        <p>
                          {messageType === "file" && messageUrl ? (
                            <a href={messageUrl} target="_blank" rel="noopener noreferrer">
                              {messageText}
                            </a>
                          ) : (
                            messageText
                          )}
                          <div className="message-timing">
                            {msg.createdAt ? moment(msg.createdAt).format("hh:mm A") : "Just now"}
                          </div>
                        </p>
                      </div>
                    </>
                  );
                })}
              <div ref={lastMessageRef} />
            </div>

            {attachedFiles.length > 0 && showDocumentList && (
              <div className="chat-uploaded-document">
                <div className="chat-uploaded-document-header">
                  <span className="chat-uploaded-document-title">
                    {attachedFiles.length} file{attachedFiles.length > 1 ? 's' : ''} attached
                  </span>
                  <button 
                    type="button" 
                    className="chat-uploaded-document-close"
                    onClick={handleToggleDocumentList}
                  >
                    <i className="iconoir-xmark"></i>
                  </button>
                </div>
                <div className="chat-uploaded-document-list">
                  {attachedFiles.map((file, index) => (
                    <div key={file.name} className="chat-uploaded-document-item">
                      <div className="chat-uploaded-document-name" title={file.name}>
                        {file.name.length > 30 ? `${file.name.slice(0, 30)}...` : file.name}
                      </div>
                      {isUploading ? (
                        <div className="chat-uploaded-document-progress">
                          <div className="circular-progress"></div>
                        </div>
                      ) : (
                        <button 
                          type="button" 
                          className="chat-uploaded-document-remove"
                          onClick={() => handleRemoveAttachedFile(index)}
                          title="Remove file"
                        >
                          <i className="iconoir-xmark"></i>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="user-chat-input">
              <input
                type="text"
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyDown={(e) => {
                  handleTyping();
                  if (e.key === "Enter") {
                    handleSendMessage();
                  }
                }}
                placeholder={typingStatus ? "Typing..." : "Send Message"}
              />
              <input
                type="file"
                ref={fileInputRef}
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.gif,.xls,.xlsx,.csv,.doc,.docx"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              {attachedFiles.length > 0 && (
                <button 
                  className="uploading-progress-btn" 
                  type="button"
                  onClick={handleToggleDocumentList}
                  disabled={isUploading}
                  title={`${attachedFiles.length} file${attachedFiles.length > 1 ? 's' : ''} attached`}
                >
                  <i className="iconoir-upload"></i>
                  {attachedFiles.length > 0 && (
                    <span className="uploading-progress-badge">{attachedFiles.length}</span>
                  )}
                </button>
              )}
              <button 
                className="attach-btn" 
                type="button" 
                onClick={handleAttachClick}
                disabled={isUploading}
              >
                <i className="iconoir-attachment"></i>
              </button>
              <button 
                className="send-btn" 
                onClick={handleSendMessage}
                disabled={isUploading}
              >
                {isUploading ? (
                  <div className="send-btn-spinner"></div>
                ) : (
                  <i className="iconoir-send"></i>
                )}
              </button>
            </div>

          </div>
        </div>
      </div>
    </Modal>
  );
};

export default UserChatScreen;
